import React, { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import uploadToCloudinary from "../../../utils/uploadToCloudinary";

const MAX_FILE_SIZE = 100 * 1024 * 1024;
const MAX_MEGAPIXELS = 200;
const MAX_CONCURRENT = 2;
const ALLOWED_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/bmp",
  "image/tiff",
  "image/webp",
  "image/heic",
  "image/heif",
]);

const uid = () => `f-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isTiff = (file) => {
  const name = file.name.toLowerCase();
  return name.endsWith(".tiff") || name.endsWith(".tif");
};

const checkDimensions = (file) =>
  new Promise((resolve) => {
    if (isTiff(file)) return resolve(null);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const mp = (img.width * img.height) / 1_000_000;
      resolve(mp > MAX_MEGAPIXELS ? `Image exceeds ${MAX_MEGAPIXELS} megapixels` : null);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve("Unable to read image dimensions");
    };
    img.src = url;
  });

const buildImagePayload = (result) => ({
  width: result.width,
  height: result.height,
  filename: result.original_filename,
  external_id: result.public_id,
  host: "cloudinary",
  source: "qr-handoff",
  url: result.secure_url,
  filesize: result.bytes,
  format: result.format,
});

const PhoneMultiUploader = ({ postImageUrl, onUploadSaved }) => {
  const [entries, setEntries] = useState([]);
  const fileInputRef = useRef(null);
  const activeUploads = useRef(0);

  const updateEntry = useCallback((id, patch) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  }, []);

  const removeEntry = useCallback((id) => {
    setEntries((prev) => {
      const entry = prev.find((e) => e.id === id);
      if (entry?.abortController) entry.abortController.abort();
      if (entry?.thumbUrl) URL.revokeObjectURL(entry.thumbUrl);
      return prev.filter((e) => e.id !== id);
    });
  }, []);

  const startUpload = useCallback(
    (entry) => {
      const controller = new AbortController();
      activeUploads.current += 1;

      updateEntry(entry.id, {
        status: "uploading",
        progress: 0,
        error: null,
        abortController: controller,
      });

      uploadToCloudinary(entry.file, {
        onProgress: (pct) => updateEntry(entry.id, { progress: pct }),
        abortSignal: controller.signal,
      })
        .then((result) => {
          updateEntry(entry.id, { status: "saving", progress: 100, abortController: null });
          return axios
            .post(postImageUrl, { image: buildImagePayload(result) })
            .then(() => {
              updateEntry(entry.id, { status: "complete", result });
              if (onUploadSaved) onUploadSaved();
            });
        })
        .catch((err) => {
          if (err.message === "Upload cancelled") return;
          updateEntry(entry.id, {
            status: "error",
            error: err.message,
            abortController: null,
          });
        })
        .finally(() => {
          activeUploads.current -= 1;
          drainQueue();
        });
    },
    [updateEntry, postImageUrl, onUploadSaved],
  );

  const drainQueue = useCallback(() => {
    setEntries((prev) => {
      const toStart = [];
      for (const entry of prev) {
        if (activeUploads.current >= MAX_CONCURRENT) break;
        if (entry.status === "pending") {
          toStart.push(entry);
        }
      }
      if (toStart.length > 0) {
        setTimeout(() => toStart.forEach((e) => startUpload(e)), 0);
      }
      return prev.map((e) =>
        toStart.find((s) => s.id === e.id) ? { ...e, status: "queued" } : e,
      );
    });
  }, [startUpload]);

  const addFiles = useCallback(async (fileList) => {
    const newEntries = [];
    for (const file of fileList) {
      const id = uid();
      const thumbUrl = URL.createObjectURL(file);

      if (!ALLOWED_TYPES.has(file.type) && !isTiff(file)) {
        newEntries.push({ id, file, thumbUrl, status: "error", progress: 0, result: null, error: "Unsupported file type", abortController: null });
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        newEntries.push({ id, file, thumbUrl, status: "error", progress: 0, result: null, error: "File exceeds 100MB limit", abortController: null });
        continue;
      }
      const dimError = await checkDimensions(file);
      if (dimError) {
        newEntries.push({ id, file, thumbUrl, status: "error", progress: 0, result: null, error: dimError, abortController: null });
        continue;
      }
      newEntries.push({ id, file, thumbUrl, status: "pending", progress: 0, result: null, error: null, abortController: null });
    }
    setEntries((prev) => [...prev, ...newEntries]);
  }, []);

  useEffect(() => {
    const hasPending = entries.some((e) => e.status === "pending");
    if (hasPending && activeUploads.current < MAX_CONCURRENT) {
      drainQueue();
    }
  }, [entries, drainQueue]);

  const retryEntry = useCallback(
    (id) => updateEntry(id, { status: "pending", progress: 0, error: null }),
    [updateEntry],
  );

  const handleFileInput = useCallback(
    (e) => {
      const files = Array.from(e.target.files || []);
      if (files.length) addFiles(files);
      e.target.value = "";
    },
    [addFiles],
  );

  const completedCount = entries.filter((e) => e.status === "complete").length;
  const inProgress = entries.some(
    (e) => e.status === "uploading" || e.status === "pending" || e.status === "queued" || e.status === "saving",
  );

  return (
    <div className="space-y-4">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileInput}
      />

      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="w-full rounded-lg px-6 py-3.5 text-base font-semibold bg-stone-800 text-white hover:bg-stone-700 transition"
      >
        {entries.length === 0 ? "Select photos" : "Add more photos"}
      </button>

      {entries.length > 0 && (
        <div className="space-y-1.5">
          {entries.map((entry) => (
            <FileRow key={entry.id} entry={entry} onRemove={removeEntry} onRetry={retryEntry} />
          ))}
        </div>
      )}

      {entries.length > 0 && (
        <p className="text-xs text-stone-400 text-center">
          {inProgress
            ? "Uploading..."
            : `${completedCount} ${completedCount === 1 ? "photo" : "photos"} sent to your computer`}
        </p>
      )}
    </div>
  );
};

const FileRow = React.memo(({ entry, onRemove, onRetry }) => {
  const { id, file, thumbUrl, status, progress, error } = entry;

  return (
    <div className="flex items-center gap-3 p-2 rounded-lg bg-stone-50">
      <div className="w-10 h-10 flex-shrink-0 rounded overflow-hidden bg-stone-200">
        {thumbUrl && <img src={thumbUrl} alt="" className="w-full h-full object-cover" />}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className="text-sm text-stone-700 truncate">{file.name}</span>
          <span className="text-xs text-stone-400 flex-shrink-0 ml-2">{formatSize(file.size)}</span>
        </div>
        {(status === "uploading" || status === "queued" || status === "saving") && (
          <div className="mt-1.5 h-1.5 w-full rounded-full bg-stone-200 overflow-hidden">
            <div
              className="h-full bg-stone-900 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
        {status === "error" && <p className="text-xs text-red-500 mt-0.5 truncate">{error}</p>}
      </div>

      <div className="flex-shrink-0 flex items-center gap-1">
        {status === "complete" && (
          <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
        {status === "saving" && (
          <span className="text-xs text-stone-400">Saving</span>
        )}
        {(status === "uploading" || status === "queued") && (
          <span className="text-xs text-stone-400 tabular-nums w-8 text-right">{progress}%</span>
        )}
        {status === "pending" && <span className="text-xs text-stone-400">Waiting</span>}
        {status === "error" && (
          <button
            onClick={() => onRetry(id)}
            className="text-xs text-stone-700 hover:text-stone-900 font-medium underline underline-offset-2"
          >
            Retry
          </button>
        )}
        {(status === "complete" || status === "error") && (
          <button
            onClick={() => onRemove(id)}
            className="p-0.5 rounded hover:bg-stone-200 transition-colors"
          >
            <svg className="w-4 h-4 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
});

FileRow.displayName = "FileRow";

export default PhoneMultiUploader;
